import { useState } from 'react'
import { useRequest, useView } from '@10000/react'
import { Avatar, AvatarDialog, Body, Box, Button, DeleteTeamDialog, TeamLevelDescriptions } from '../../widgets'
import { DropDown, Location, Text } from '../../forms'
import { isPartner, levels, roles } from '../../../lookups'
import { SettingsForm } from '../'
import { TeamLeadSelect, OrganisationSelect } from './'
import { randomInt } from '@10000/common'

export const Team = ({ superUserMode, teamId, organisationId, onClose }) => {
  const request = useRequest('tenant')
  const { user, teams, subscription, organisations, company } = useView('tenant')
  const partner = isPartner(subscription)
  const createdTeam = teams.findIndex(team => team.id === teamId) > -1
  const admin = superUserMode || user?.admin

  const formattedRoles = roles.map((role, index) => ({ label: role.name, value: index }))
  const formattedLevels = levels.map((level, index) => ({ label: level.name, value: index }))

  const getOrganisation = (organisation) => organisations.find(x => x.id === organisationId || organisation) || {}

  const { name, country, city, level, role, leaders, avatar, organisation } = teams.find(team => team?.id === teamId)
  || { country: getOrganisation()?.country || company?.country, city: getOrganisation()?.city || company?.city }

  const selectedOrganisation = organisationId || organisation

  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false)
  const [selectedAvatar, setSelectedAvatar] = useState(avatar !== undefined ? avatar : randomInt(6))

  const [selectedLeaders, setSelectedLeaders] = useState(leaders || [])
  const [newLeaders, setNewLeaders] = useState([])
  const [message, setMessage] = useState('')

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const avatarSelected = (avatar) => {
    setSelectedAvatar(avatar)
    setAvatarDialogOpen(false)
  }

  const saveTeam = ({ name, country, city, level, role }) => {
    if((selectedLeaders.length + newLeaders.length) > 0 && ((selectedOrganisation && partner) || !partner)) {
      request(
        createdTeam ? 'teamUpdated' : 'teamAdded',
        { id: teamId, name, country, city, level, role, avatar: selectedAvatar, leaders: selectedLeaders, organisation: selectedOrganisation }
      ).then(() => {
          Promise.all(newLeaders.map(email => request('userAdded', { teamId, email }))).then(() => {
            setMessage('')
            onClose()
          })
        })
    } else {
      setMessage((!selectedOrganisation && partner) ? 'This team must have an organisation selected' : 'This team must have a leader assigned.')
    }
  }

  return (
    <SettingsForm
      onSubmit={saveTeam}
      values={{ name, country, city, level, role, leaders, avatar, organisation }}
      title={name || 'New team'}
      onBackButtonClick={onClose}
    >
      <Box gap={16} alignItems="center" justifyContent="flex-start" fullWidth >
        <Avatar index={selectedAvatar} mode="team"/>
        <Button className="small" onClick={() => setAvatarDialogOpen(true)}>
          Change avatar
        </Button>
      </Box>

      <Text title="Name" name="name" value={name} dark required/>

      <DropDown fullWidth dark name="role" title="Function" required options={formattedRoles}/>
      <DropDown fullWidth dark name="level" title="Level" required options={formattedLevels}/>
      <TeamLevelDescriptions color="black" />

      <Location country={country} dark />

      {partner && (
        <OrganisationSelect
          organisation={selectedOrganisation}
          editable={false}
        />
      )}

      <Box gap={8} column>
        <TeamLeadSelect
          superUserMode={superUserMode}
          selectedLeaders={selectedLeaders}
          onLeadersSelected={setSelectedLeaders}
          newLeaders={newLeaders}
          onNewLeaderSelected={setNewLeaders}
        />
        {message && <Body color="red">{message}</Body>}

        {(createdTeam && admin) && (
          <Box fullWidth p="32px 0" justifyContent="flex-start">
            <Box onClick={() => setDeleteDialogOpen(true)} cursor="pointer">
              <Body underline bold alt italic>Delete Team</Body>
            </Box>
          </Box>
        )}
      </Box>

      <AvatarDialog
        value={avatar}
        mode="team"
        open={avatarDialogOpen}
        onClose={avatarSelected}
      />
      <DeleteTeamDialog
        open={deleteDialogOpen}
        onDelete={() => {
          setDeleteDialogOpen(false)
          onClose()
        }}
        onClose={() => setDeleteDialogOpen(false)}
        id={teamId}
      />
    </SettingsForm>
  )
}